import { AppError } from '@/stores/appErrorStore';
import {
    APPROVE_SUCCESS_MS,
    type HipSpineXRayModalContext,
} from '@/views/3d-planner/hipSpine/xrayModal/modalState';
import { useStandingAnteversionPromptViewModel } from '@/views/3d-planner/hipSpine/useStandingAnteversionPromptViewModel';

/**
 * The modal's async server operations (Approve, Reset) plus the panel-view reset.
 * Each closes over the shared {@link HipSpineXRayModalContext} so the in-flight flags they
 * mutate are the same ones the gating computeds read.
 */
export function createModalActions(ctx: HipSpineXRayModalContext) {
    const { store, editVm, isResetting, resetError, isApproving, approveSuccess, approveError } =
        ctx;

    async function handleReset(): Promise<void> {
        const postureId = store.activePostureId;
        if (!postureId) return;
        isResetting.value = true;
        resetError.value = null;
        try {
            // Drop client overrides FIRST so the posture is no longer dirty — this
            // neutralises any debounced auto-save for it (the sync loop saves from
            // `dirtyPostureIds`), so a queued save can't fire mid-reset and re-PUT the
            // discarded edits. Combined with the `isSaving` gate above, this closes the
            // reset-vs-save race. resetPostureToAi re-applies server truth + geometry.
            editVm.clearPostureOverrides(postureId);
            await store.resetPostureToAi(postureId);
        } catch (error) {
            resetError.value =
                error instanceof AppError ? error.displayMessage : 'Failed to reset to AI.';
            // Reset didn't land; re-render from the now-cleared overlay state (the
            // last-saved server values) so the view doesn't keep showing dropped edits.
            editVm.rebuildAndSync(postureId);
        } finally {
            isResetting.value = false;
        }
    }

    async function handleApprove(): Promise<void> {
        if (!store.activePostureId) return;
        isApproving.value = true;
        approveError.value = null;
        // Snapshot approval state BEFORE approving so the prompt can arm only on the false→true
        // edge (a re-approve with nothing pending must not arm).
        const wasAllApproved = store.allPosturesApproved;
        try {
            await store.approveLandmarks(store.activePostureId);
            approveSuccess.value = true;
            setTimeout(() => (approveSuccess.value = false), APPROVE_SUCCESS_MS);
            // Resolve the prompt ViewModel lazily and call by property so the decision logic stays
            // in the ViewModel (MVVM) — never reached during study load or polling, only here.
            useStandingAnteversionPromptViewModel().evaluateAfterApproval(wasAllApproved);
        } catch (error) {
            approveError.value =
                error instanceof AppError ? error.displayMessage : 'Failed to approve annotations.';
        } finally {
            isApproving.value = false;
        }
    }

    // Called from the View's onMounted so re-opening the modal always lands on
    // the Annotations tab (the watch only fires on a posture *change*).
    function resetPanelView(): void {
        store.setActivePanelView('annotations');
    }

    return { handleReset, handleApprove, resetPanelView };
}
